import * as React from 'react';
import { styled } from '@linaria/react';

import { clamp } from '@/utils';
import useBoundingBox from '@/hooks/use-bounding-box';

import { SWEEPER_WIDTH } from './DeferredValueTimeline.constants';
import {
  useScrubbing,
  calculateClipPath,
  calculateSweeperStyle,
} from './DeferredValueTimeline.helpers';
import RenderEvent from './RenderEvent';
import Ticker from './Ticker';
import SweeperSvg from './SweeperSvg';
import Click from './Click';
import { Render, FrameType } from './DeferredValueTimeline.types';

interface Props {
  id: string;
  renders: Array<Render>;
  clicks: Array<number>;
  currentFrame: number;
  totalNumOfFrames: number;
  isScrubbingDisabled?: boolean;
  setCurrentFrame: React.Dispatch<React.SetStateAction<number>>;
  handleScrubStart: () => void;
  handleTogglePlay: () => void;
}

function Timeline({
  id,
  renders,
  clicks,
  currentFrame,
  totalNumOfFrames,
  isScrubbingDisabled = false,
  setCurrentFrame,
  handleScrubStart,
  handleTogglePlay,
}: Props) {
  const [isScrubbing, setIsScrubbing] = React.useState(false);
  const scrubStartEvent = React.useRef<React.MouseEvent | null>(null);

  const clipperRef = React.useRef<HTMLDivElement>(null);
  const clipperBB = useBoundingBox(clipperRef);

  // `currentFrame` is 0-indexed, so we need to subtract 1 from the total to get the final frame number.
  const finalFrame = totalNumOfFrames - 1;

  const timeElapsedPercentage = (currentFrame / finalFrame) * 100;

  let frameType: FrameType = 'mid';
  if (currentFrame === 0) {
    frameType = 'start';
  } else if (currentFrame === finalFrame) {
    frameType = 'end';
  }

  useScrubbing({
    id,
    isScrubbing,
    clipperBB,
    currentFrame,
    finalFrame,
    setCurrentFrame,
    setIsScrubbing,
    isScrubbingDisabled: false,
    scrubStartEvent,
  });

  return (
    <div
      onMouseDown={(event) => {
        event.preventDefault();

        if (!isScrubbingDisabled) {
          setIsScrubbing(true);
          handleScrubStart();
          scrubStartEvent.current = event;
        }
      }}
      onTouchStart={(event) => {
        event.preventDefault();

        if (!isScrubbingDisabled) {
          setIsScrubbing(true);
          handleScrubStart();
        }
      }}
    >
      <Wrapper
        style={{ cursor: isScrubbing ? 'grabbing' : undefined }}
      >
        <Clipper
          ref={clipperRef}
          style={{
            clipPath: calculateClipPath(timeElapsedPercentage),
            cursor: isScrubbingDisabled
              ? 'not-allowed'
              : isScrubbing
                ? 'grabbing'
                : undefined,
          }}
        >
          <Background />
          {renders.map((render) => {
            const startedByClick = clicks.includes(render.start);
            return (
              <RenderEvent
                render={render}
                key={render.start}
                currentFrame={currentFrame}
                totalNumOfFrames={totalNumOfFrames}
                startedByClick={startedByClick}
              />
            );
          })}
        </Clipper>
        <ClicksWrapper>
          {clicks
            .filter((frameNum) => frameNum <= currentFrame)
            .map((frameNum) => {
              return (
                <Click
                  key={frameNum}
                  frameNum={frameNum}
                  totalNumOfFrames={totalNumOfFrames}
                />
              );
            })}
        </ClicksWrapper>
        <Sweeper
          data-sweeper="true"
          type="button"
          style={calculateSweeperStyle(
            timeElapsedPercentage,
            isScrubbing,
            isScrubbingDisabled,
            clipperBB
          )}
          onMouseDown={(ev: React.MouseEvent<HTMLButtonElement>) => {
            setIsScrubbing(true);

            // For some reason, the sweeper doesn't get focus when clicked, so we do it manually.
            ev.currentTarget.focus();
          }}
          onKeyDown={(event) => {
            if (isScrubbingDisabled) {
              return;
            }

            if (event.code === 'Space') {
              handleTogglePlay();
            } else if (event.code === 'ArrowRight') {
              event.preventDefault();
              setCurrentFrame((frame: number) =>
                clamp(frame + 1, 0, finalFrame)
              );
            } else if (event.code === 'ArrowLeft') {
              event.preventDefault();
              setCurrentFrame((frame: number) =>
                clamp(frame - 1, 0, finalFrame)
              );
            }
          }}
        >
          <SweeperSvg frameType={frameType} />
        </Sweeper>
      </Wrapper>
      <Ticker totalNumOfFrames={totalNumOfFrames} />
    </div>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 6rem;
  cursor: ew-resize;
  touch-action: pan-x;
`;

const Clipper = styled.div`
  will-change: transform;
  height: 100%;
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  /* So this is super subtle: when fully extended, the sweeper covers the right edge, so we don't see the corner radius. Move it slightly further from the right so we get a hint of rounding */
  right: 2px;
  background: hsl(210deg 15% 20%);
  border-radius: 4px 4px 0px 0px;
`;

const ClicksWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  pointer-events: none;
`;

const Sweeper = styled.button`
  position: absolute;
  top: -8px;
  left: 0;
  width: ${SWEEPER_WIDTH}px;
  will-change: transform;
  cursor: grab;

  &:focus-visible {
    /* Focus state indicated by “SweeperSvg” */
    outline: none;
  }

  &:active {
    cursor: grabbing;
  }
`;

export default Timeline;
